import { Box, Divider, Stack, Text } from '@mantine/core';
import { Apart } from 'components/Apart';
import { Panel } from 'components/Panel';
import { tKeys, useTranslation } from 'locales';
import type { UnhandledInvoice } from 'modules/AtRisk/api/use-load-unhandled-client';
import { prop, sumBy } from 'remeda';
import { useLocalizedCurrencyFormatter } from 'shared/utils/currency';
import { ActionButton } from './ActionButton';
import { InvoiceCard } from './InvoiceCard';
import type { ActionButtonGroup } from './use-build-action-buttons';

export const ACTION_BUTTON_MIN_WIDTH = 200;

type UnhandledInvoicesLayoutProps = {
  actionButtonGroups: Array<ActionButtonGroup>;
  invoices: Array<UnhandledInvoice>;
};

export const UnhandledInvoicesLayout = ({
  actionButtonGroups,
  invoices,
}: UnhandledInvoicesLayoutProps) => {
  const { t } = useTranslation();
  const formatter = useLocalizedCurrencyFormatter();

  const invoicesTotal = sumBy(invoices, prop('totalTvac'));

  const INVOICES = invoices.map(invoice => <InvoiceCard key={invoice.id} invoice={invoice} />);

  const ACTION_GROUPS = actionButtonGroups.map(group => (
    <Stack key={group.translationKey} gap="xs">
      <Divider
        labelPosition="left"
        label={
          <Text size="xs" c="gray.5" fw={600} tt="uppercase" lts={0.6}>
            {t(group.translationKey)}
          </Text>
        }
      />
      <Box display="grid" style={ACTION_BUTTON_GRID_STYLE}>
        {group.definitions.map(definition => (
          <ActionButton key={definition.translationKey} definition={definition} />
        ))}
      </Box>
    </Stack>
  ));

  return (
    <Panel fullHeight pt={12}>
      <Stack gap="md">{ACTION_GROUPS}</Stack>
      <Divider orientation="horizontal" my="md" />
      <Box bdrs={8} bd="1px solid var(--mantine-color-gray-2)">
        <Box
          px={8}
          bg="purple.0"
          py={6}
          style={{
            borderBottom: '1px solid var(--mantine-color-gray-2)',
            borderRadius: '8px 8px 0px 0px',
          }}
        >
          <Apart>
            <Text size="xs" c="gray.5" fw="bold">
              {t(tKeys.MODULES.AT_RISK.OVERVIEW.INVOICES_COUNT, { count: invoices.length })}
            </Text>
            <Text size="xs" c="dimmed">
              {t(tKeys.MODULES.AT_RISK.SELECTION_NEXT_STEP)}
            </Text>
          </Apart>
        </Box>
        {INVOICES}
        <Box px={8} py={6} style={{ borderTop: '1px solid var(--mantine-color-gray-2)' }}>
          <Apart>
            <Text size="xs" c="gray.5" fw="bold">
              {t(tKeys.TOTAL)}
            </Text>
            <Text size="xs" fw="bold">
              {formatter.format(invoicesTotal)}
            </Text>
          </Apart>
        </Box>
      </Box>
    </Panel>
  );
};

const ACTION_BUTTON_GRID_STYLE = {
  gridTemplateColumns: `repeat(auto-fill, minmax(${ACTION_BUTTON_MIN_WIDTH}px, 1fr))`,
  gap: 'var(--mantine-spacing-md)',
};
